<script setup>
// Chrome for solar-angebot-vergleich.de - shares its masthead component
// (SolarangebotvergleichNav) with the homepage so every page on the host
// renders the same nav. Legal pages (Impressum/Datenschutz/AGB/Widerruf)
// hit this wrapper through VariantPageWrapper; the homepage uses the same
// nav component inline. Body markup of the wrapped page is yielded via
// <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';
import SolarangebotvergleichNav from './parts/SolarangebotvergleichNav.vue';
import SolarangebotvergleichFooter from './parts/SolarangebotvergleichFooter.vue';
import Breadcrumb from '@/Components/Breadcrumb.vue';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: 'Solar-Angebot-Vergleich' },
    brandTagline: { type: String, default: 'Photovoltaik-Angebote ehrlich vergleichen.' },
    brandAccent: { type: String, default: '#FACC15' },
    brandLogo: { type: String, default: '' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || 'Solar-Angebot-Vergleich');
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);

const fontsHref = 'https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;600;700;800&family=Inter:wght@400;500;600;700&display=swap';
</script>

<template>
    <div class="sav" :style="`--sav-accent:${brandAccent};--brand-accent:${brandAccent};--brand-accent-dark:#EAB308;--brand-accent-soft:rgba(250,204,21,0.10);--brand-accent-text:#0F1A35;--brand-accent-fg:#0F1A35;`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>

        <SolarangebotvergleichNav :brand-name="brandName" />

        <Breadcrumb />

        <slot />

        <SolarangebotvergleichFooter :brand-name="brandName" :author="author" />
    </div>
</template>

<style scoped>
.sav {
    --sav-ink: #0F1A35;
    --sav-ink-2: #25325C;
    --sav-ink-3: #5C6989;
    --sav-bg: #FFFFFF;
    --sav-bg-2: #FAF8F1;
    --sav-line: #E5E0CC;
    --sav-accent-deep: #EAB308;
    --sav-cta-ink: #0F1A35;
    --sav-sun: #FACC15;
    --sav-notice-bg: #0F1A35;
    font-family: 'Source Sans 3', 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
    color: var(--sav-ink);
    background: var(--sav-bg);
    line-height: 1.6;
    min-height: 100vh;
}
.sav *, .sav *::before, .sav *::after { box-sizing: border-box; }

/* ── Brand-paint shared sub-pages (Blog / BlogPost / Glossary / Contact)
 * so the article body inherits the SAV palette instead of falling back
 * to the generic ds-article cream/grey tokens. Uses :deep() so the
 * overrides land on slot-rendered children without leaking outside the
 * .sav root. */
.sav :deep(.ds-article),
.sav :deep(.ds-article__card),
.sav :deep([style*="background: #FAF8F4"]) { background: var(--sav-bg-2, #FAF8F1) !important; }
.sav :deep(.ds-article__card) { border-color: var(--sav-line, #E5E0CC) !important; box-shadow: 0 18px 48px -28px rgba(15,26,53,0.18) !important; }
.sav :deep(.ds-article h2),
.sav :deep(.ds-article h3),
.sav :deep(.ds-article__title),
.sav :deep([style*="color: #1A1A17"]) { color: var(--sav-ink, #0F1A35) !important; }
.sav :deep(.ds-article h2) {
    border-bottom: 3px solid var(--sav-accent, #FACC15);
    padding-bottom: 8px;
    margin-top: 32px;
}
.sav :deep(.ds-article h3) {
    color: var(--sav-accent-deep, #EAB308) !important;
    margin-top: 22px;
}
.sav :deep(.ds-article a) { color: var(--sav-accent-deep, #EAB308) !important; text-decoration: underline; text-underline-offset: 2px; }
.sav :deep(.ds-article a:hover) { color: var(--sav-ink, #0F1A35) !important; }
.sav :deep(.ds-article ul li::marker),
.sav :deep(.ds-article ol li::marker) { color: var(--sav-accent-deep, #EAB308); }
.sav :deep(.ds-article blockquote) {
    border-left: 4px solid var(--sav-accent, #FACC15) !important;
    background: rgba(250,204,21,0.08) !important;
    color: var(--sav-ink, #0F1A35) !important;
}
/* Breadcrumb row at top of BlogPost - use sav-ink-3 grey rather than
 * the generic muted grey so the row sits in the SAV palette. */
.sav :deep([style*="color: #6B6B66"]) { color: var(--sav-ink-3, #5C6989) !important; }
/* Bottom CTA banner ("Bereit für Ihr Photovoltaik-Angebot?") gets the
 * SAV navy + sun-yellow button treatment instead of the generic dark
 * editorial card. */
.sav :deep(.ds-cta), .sav :deep(.ds-cta-banner) {
    background: var(--sav-notice-bg, #0F1A35) !important;
    color: #fff !important;
}
.sav :deep(.ds-cta .btn), .sav :deep(.ds-cta button), .sav :deep(.ds-cta a) {
    background: var(--sav-accent, #FACC15) !important;
    color: var(--sav-cta-ink, #0F1A35) !important;
    border-color: var(--sav-accent, #FACC15) !important;
}

</style>
